import { useState } from 'react'
import { useCriarItem, useDefinirItemAtivo, useItensDaLista, useRenomearItem } from '../../../api/queries/referenciasAdmin'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { Cartao } from '../../../components/ListaDeDados'
import { CampoDeTexto } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import type { ItemNaAdministracao, ListaDeApoio as Qual } from '../../../types/usuario'

/**
 * Uma lista de apoio: sistemas ou tipos de manutenção.
 *
 * **Não há exclusão, e isso é decisão e não limitação.** Um item já usado por uma solicitação tem
 * chave estrangeira apontando para ele; apagá-lo é impossível — e, se fosse possível, seria pior:
 * o registro histórico passaria a citar um sistema que "nunca existiu". Desativar tira da lista de
 * escolha e deixa intacto o que já foi registrado.
 *
 * **O código não é editável.** Renomear é Regra nº 4 — rótulo é string de tela. O código é
 * referência de dado, e trocá-lo quebraria integração, filtro salvo e relatório que o cite.
 *
 * @param props qual lista e como chamá-la na tela
 * @returns o cartão da lista
 */
export function ListaDeApoio({ qual, titulo, ajuda }: { qual: Qual; titulo: string; ajuda: string }) {
  const { data: itens, isPending, isError, error } = useItensDaLista(qual)
  const criar = useCriarItem(qual)
  const renomear = useRenomearItem(qual)
  const ativar = useDefinirItemAtivo(qual)
  const [codigo, setCodigo] = useState('')
  const [rotulo, setRotulo] = useState('')

  async function cadastrar() {
    try {
      await criar.mutateAsync({ codigo, rotulo })
      setCodigo('')
      setRotulo('')
    } catch {
      // O AvisoDeErro abaixo diz o que o servidor recusou.
    }
  }

  return (
    <Cartao titulo={titulo} descricao={ajuda}>
      {isPending && <Esqueleto linhas={4} />}
      {isError && <AvisoDeErro erro={error} titulo={TEXTOS.referencias.naoCarregou} />}

      {itens && (
        <ul className="divide-y divide-slate-100">
          {itens.map((item) => (
            <LinhaDoItem
              key={item.id}
              item={item}
              aoRenomear={(novo) => renomear.mutate({ id: item.id, rotulo: novo })}
              aoAlternar={() => ativar.mutate({ id: item.id, ativo: !item.ativo })}
              ocupado={renomear.isPending || ativar.isPending}
            />
          ))}
        </ul>
      )}

      <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <CampoDeTexto
            rotulo="Código"
            ajuda={TEXTOS.referencias.codigoAjuda}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
          />
          <CampoDeTexto
            rotulo="Nome na tela"
            ajuda={TEXTOS.referencias.rotuloAjuda}
            value={rotulo}
            onChange={(e) => setRotulo(e.target.value)}
          />
        </div>
        <Botao
          aparencia="primario"
          onClick={cadastrar}
          disabled={criar.isPending || !codigo.trim() || !rotulo.trim()}
        >
          {criar.isPending ? 'Cadastrando…' : 'Cadastrar'}
        </Botao>
        {criar.isError && <AvisoDeErro erro={criar.error} titulo={TEXTOS.referencias.naoCadastrou} />}
      </div>

      {(renomear.isError || ativar.isError) && (
        <AvisoDeErro erro={renomear.error ?? ativar.error} titulo={TEXTOS.referencias.naoSalvou} />
      )}
    </Cartao>
  )
}

/** Uma linha da lista, com renomear no lugar e o botão de ativar/desativar. */
function LinhaDoItem({
  item,
  aoRenomear,
  aoAlternar,
  ocupado,
}: {
  item: ItemNaAdministracao
  aoRenomear: (rotulo: string) => void
  aoAlternar: () => void
  ocupado: boolean
}) {
  const [editando, setEditando] = useState(false)
  const [rotulo, setRotulo] = useState(item.rotulo)

  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <span className="min-w-0 flex-1">
        {editando ? (
          <CampoDeTexto rotulo="Nome na tela" value={rotulo} onChange={(e) => setRotulo(e.target.value)} />
        ) : (
          <>
            <span className={`block text-sm ${item.ativo ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
              {item.rotulo}
            </span>
            <span className="block font-mono text-xs text-slate-400">{item.codigo}</span>
          </>
        )}
      </span>

      <span className="flex shrink-0 gap-2">
        {editando ? (
          <>
            <Botao
              tamanho="pequeno"
              aparencia="primario"
              onClick={() => {
                aoRenomear(rotulo.trim())
                setEditando(false)
              }}
              disabled={ocupado || !rotulo.trim()}
            >
              Salvar
            </Botao>
            <Botao
              tamanho="pequeno"
              onClick={() => {
                setRotulo(item.rotulo)
                setEditando(false)
              }}
            >
              Cancelar
            </Botao>
          </>
        ) : (
          <>
            <Botao tamanho="pequeno" onClick={() => setEditando(true)}>
              Renomear
            </Botao>
            <Botao tamanho="pequeno" onClick={aoAlternar} disabled={ocupado}>
              {item.ativo ? 'Desativar' : 'Reativar'}
            </Botao>
          </>
        )}
      </span>
    </li>
  )
}
